<template>
  <div class="payments-wrapper" ref="paymentsWrapper">
    <!-- Skeleton Loading State -->
    <div class="payments-container" v-if="loading">
      <div class="payments-header skeleton-header">
        <div class="contract-info">
          <div class="skeleton-icon"></div>
          <div class="contract-details">
            <div class="skeleton-text skeleton-label"></div>
            <div class="skeleton-text skeleton-title"></div>
          </div>
        </div>
        <div class="payment-summary">
          <div class="summary-item">
            <div class="skeleton-text skeleton-number"></div>
            <div class="skeleton-text skeleton-small"></div>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-item">
            <div class="skeleton-text skeleton-number"></div>
            <div class="skeleton-text skeleton-small"></div>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-item">
            <div class="skeleton-text skeleton-number"></div>
            <div class="skeleton-text skeleton-small"></div>
          </div>
        </div>
      </div>

      <!-- Desktop Skeleton Table -->
      <div class="payments-table-wrapper d-none d-lg-block">
        <table class="payments-table">
          <thead>
            <tr>
              <th>Descripción</th>
              <th>Monto</th>
              <th>Vencimiento</th>
              <th>Estado</th>
              <th v-if="showActionColumn">Acción</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="n in 5" :key="'skeleton-row-' + n" class="skeleton-row">
              <td><div class="skeleton-text" style="width: 140px"></div></td>
              <td><div class="skeleton-text" style="width: 80px"></div></td>
              <td><div class="skeleton-text" style="width: 100px"></div></td>
              <td><div class="skeleton-badge"></div></td>
              <td v-if="showActionColumn"><div class="skeleton-button"></div></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile Skeleton Cards -->
      <div class="payments-cards d-lg-none">
        <div
          v-for="n in 4"
          :key="'skeleton-card-' + n"
          class="payment-card skeleton-card"
        >
          <div class="card-header">
            <div class="card-title">
              <div class="skeleton-text" style="width: 120px"></div>
              <div
                class="skeleton-text skeleton-small"
                style="width: 80px"
              ></div>
            </div>
            <div class="skeleton-badge"></div>
          </div>
          <div class="card-body">
            <div class="card-detail">
              <div
                class="skeleton-text skeleton-small"
                style="width: 50px"
              ></div>
              <div class="skeleton-text" style="width: 70px"></div>
            </div>
            <div class="card-detail">
              <div
                class="skeleton-text skeleton-small"
                style="width: 70px"
              ></div>
              <div class="skeleton-text" style="width: 90px"></div>
            </div>
          </div>
          <div class="card-footer" v-if="showActionColumn">
            <div class="skeleton-button-full"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Actual Content -->
    <div
      class="payments-container"
      v-else-if="payment_data.payments.length > 0 && project"
    >
      <!-- Contract Header -->
      <div class="payments-header">
        <div class="contract-info">
          <div class="contract-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
              <path
                d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5C15 6.10457 14.1046 7 13 7H11C9.89543 7 9 6.10457 9 5Z"
                stroke="currentColor"
                stroke-width="1.5"
              />
              <path
                d="M9 12H15M9 16H13"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <div class="contract-details">
            <span class="contract-label">Contrato</span>
            <span class="contract-number"
              >Nº
              {{
                new Intl.NumberFormat("es-CL").format(
                  payment_data.contract_number
                )
              }}</span
            >
          </div>
        </div>

        <!-- Payment Summary -->
        <div class="payment-summary">
          <div class="summary-item">
            <span class="summary-value">{{ paidCount }}</span>
            <span class="summary-label">Pagados</span>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-item">
            <span class="summary-value warning">{{ pendingCount }}</span>
            <span class="summary-label">Pendientes</span>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-item">
            <span class="summary-value danger">{{ overdueCount }}</span>
            <span class="summary-label">Atrasados</span>
          </div>
        </div>
      </div>

      <b-overlay :show="loading" rounded="lg" spinner-variant="primary">
        <!-- Desktop Table View -->
        <div class="payments-table-wrapper d-none d-lg-block">
          <table class="payments-table">
            <thead>
              <tr>
                <th>Descripción</th>
                <th>Monto</th>
                <th>Vencimiento</th>
                <th>Estado</th>
                <th v-if="showActionColumn">
                  {{ country == 0 ? "Comprobante" : "Acción" }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(payment, key) in pageOfItems"
                :key="'pay-tr' + key"
                :class="getRowClass(payment)"
              >
                <td class="payment-description">
                  <div class="description-content">
                    <span class="description-text">{{
                      payment.descripcionPago
                    }}</span>
                    <span v-if="payment.pagoCuota" class="description-quota"
                      >Cuota {{ payment.cuota }}</span
                    >
                  </div>
                </td>
                <td class="payment-amount">
                  <span class="amount-value">
                    {{
                      parseFloat(payment.montoPago) > 0
                        ? new Intl.NumberFormat("es-CL").format(
                            payment.montoPago
                          )
                        : 0
                    }}
                  </span>
                  <span class="amount-currency">{{
                    country == 0 ? "" : "UF"
                  }}</span>
                </td>
                <td class="payment-date">
                  {{ formatDate(payment.fechaVencimientoPago) }}
                </td>
                <td class="payment-status">
                  <span :class="getStatusClass(payment)">
                    {{ getStatusText(payment) }}
                  </span>
                </td>
                <td class="payment-action" v-if="showActionColumn">
                  <a
                    v-if="is_payed(payment)"
                    :href="payment.urlReport"
                    target="_blank"
                    class="action-btn receipt"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 10V16M12 16L9 13M12 16L15 13"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L18.7071 8.70711C18.8946 8.89464 19 9.149 19 9.41421V19C19 20.1046 18.1046 21 17 21Z"
                        stroke="currentColor"
                        stroke-width="1.5"
                      />
                    </svg>
                    <span>{{ country == 0 ? "Boleta" : "Recibo" }}</span>
                  </a>
                  <a
                    v-else-if="canPay(payment)"
                    :href="`${integrationUrl}otrospagos/link?rut=${rut}&customer_id=${customerId}&project_id=${apartment.project_id}&version=${gcVersion}`"
                    target="_blank"
                    class="action-btn pay"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <rect
                        x="2"
                        y="5"
                        width="20"
                        height="14"
                        rx="2"
                        stroke="currentColor"
                        stroke-width="1.5"
                      />
                      <path
                        d="M2 10H22"
                        stroke="currentColor"
                        stroke-width="1.5"
                      />
                    </svg>
                    <span>Pagar</span>
                  </a>
                  <span v-else class="no-action">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile Card View -->
        <div class="payments-cards d-lg-none">
          <div
            v-for="(payment, key) in pageOfItems"
            :key="'mobile-pay-' + key"
            class="payment-card"
            :class="getRowClass(payment)"
          >
            <div class="card-header">
              <div class="card-title">
                <span class="description-text">{{
                  payment.descripcionPago
                }}</span>
                <span v-if="payment.pagoCuota" class="description-quota"
                  >Cuota {{ payment.cuota }}</span
                >
              </div>
              <span :class="getStatusClass(payment)">
                {{ getStatusText(payment) }}
              </span>
            </div>

            <div class="card-body">
              <div class="card-detail">
                <span class="detail-label">Monto</span>
                <span class="detail-value">
                  {{
                    parseFloat(payment.montoPago) > 0
                      ? new Intl.NumberFormat("es-CL").format(payment.montoPago)
                      : 0
                  }}
                  {{ country == 0 ? "" : "UF" }}
                </span>
              </div>
              <div class="card-detail">
                <span class="detail-label">Vencimiento</span>
                <span class="detail-value">{{
                  formatDate(payment.fechaVencimientoPago)
                }}</span>
              </div>
            </div>

            <div class="card-footer" v-if="showActionColumn">
              <a
                v-if="is_payed(payment)"
                :href="payment.urlReport"
                target="_blank"
                class="action-btn receipt full-width"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 10V16M12 16L9 13M12 16L15 13"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L18.7071 8.70711C18.8946 8.89464 19 9.149 19 9.41421V19C19 20.1046 18.1046 21 17 21Z"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                </svg>
                <span>Ver {{ country == 0 ? "Boleta" : "Recibo" }}</span>
              </a>
              <a
                v-else-if="canPay(payment)"
                :href="`${integrationUrl}otrospagos/link?rut=${rut}&customer_id=${customerId}&project_id=${apartment.project_id}&version=${gcVersion}`"
                target="_blank"
                class="action-btn pay full-width"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <rect
                    x="2"
                    y="5"
                    width="20"
                    height="14"
                    rx="2"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <path d="M2 10H22" stroke="currentColor" stroke-width="1.5" />
                </svg>
                <span>Pagar Ahora</span>
              </a>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div class="payments-pagination">
          <jw-pagination
            :pageSize="10"
            :items="payment_data.payments"
            @changePage="onChangePage"
            :styles="customStyles"
            :labels="customLabels"
          ></jw-pagination>
        </div>
      </b-overlay>
    </div>
  </div>
</template>
<script>
export default {
  name: "paymentsOtrosPagos",
  components: {},
  props: {
    asset_id: null,
    showActionColumn: { type: Boolean, default: true },
  },
  data() {
    return {
      showPayment: true,
      isFirstLoad: true,
      customStyles: {
        ul: {
          display: "flex",
          justifyContent: "center",
          gap: "4px",
          padding: "0",
          margin: "0",
          listStyle: "none",
        },
        li: {
          display: "inline-block",
        },
        a: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "36px",
          height: "36px",
          borderRadius: "8px",
          color: "#4b5563",
          textDecoration: "none",
          fontSize: "0.875rem",
          fontWeight: "500",
          transition: "all 0.2s ease",
          border: "1px solid #e5e7eb",
          background: "#ffffff",
        },
      },
      customLabels: {
        first: "«",
        last: "»",
        previous: "‹",
        next: "›",
      },
      transbank: {
        token: "",
        url: "",
        monto: "",
        concept: "",
        back_url: window.location.href,
        id: "",
        loading: false,
      },
      pageOfItems: [],
      Apartment: null,
      loading: true,
      payment_data: {
        contract_number: "",
        payments: [],
        loading: null,
      },
      rut: this.$auth.watch.data.rut.replace(/-/g, ""),
    };
  },
  mounted() {
    let self = this;
    this.axios.get("payments/").then((result) => {
      result.data.map(function (value) {
        self.loading = false;
        value.bienes.map(function (v) {
          if (self.asset_id == v.id) {
            self.payment_data.contract_number = value.id;
            self.Apartment = v;
            self.payment_data.payments = value.pagos.filter(function (pago) {
              return self.payment_valid(pago);
            });
            self.payment_data.loading = false;
            self.$store.commit("setContractId", value.id);
          }
        });
      });
    });
  },
  methods: {
    formatDate(date) {
      const dateStr = this.gcVersion == 1 ? date : date.toString().slice(0, 10);
      return this.$moment(dateStr).format("DD MMM YYYY");
    },
    getRowClass(payment) {
      if (this.is_payed(payment)) return "row-paid";
      if (
        parseFloat(payment.montoPago) > 0 &&
        payment.fechaVencimientoPago < this.date_today &&
        !["Crédito Hipotecario", "Pie Crédito Directo", "Pie Promesa"].includes(
          payment.descripcionPago
        )
      )
        return "row-overdue";
      return "";
    },
    payment_valid(payment) {
      let payments_banned = [
        "cuota de ajuste",
        "crédito hipotecario",
        "subsidio",
        "comision broker",
        "bono pie",
        "gift card",
        "bono galco",
        "aporte inmobiliario",
        "aporte inmobiliaria",
      ];
      let descripcion = (payment.descripcionPago || "").toLowerCase();
      return !payments_banned.includes(descripcion);
    },
    getStatusClass(payment) {
      if (this.is_payed(payment)) return "status-badge paid";
      if (
        parseFloat(payment.montoPago) > 0 &&
        payment.fechaVencimientoPago < this.date_today &&
        !["Crédito Hipotecario", "Pie Crédito Directo", "Pie Promesa"].includes(
          payment.descripcionPago
        )
      )
        return "status-badge overdue";
      if (this.automated && payment.pagoCuota) return "status-badge scheduled";
      if (
        parseFloat(payment.montoPago) > 0 &&
        (payment.pagoCuota || this.customer == "clab")
      )
        return "status-badge pending";
      return "status-badge neutral";
    },
    getStatusText(payment) {
      if (this.is_payed(payment)) return this.text_payed(payment);
      if (
        parseFloat(payment.montoPago) > 0 &&
        payment.fechaVencimientoPago < this.date_today &&
        !["Crédito Hipotecario", "Pie Crédito Directo", "Pie Promesa"].includes(
          payment.descripcionPago
        )
      )
        return "Atrasado";
      if (this.automated && payment.pagoCuota) return "Programado";
      if (
        parseFloat(payment.montoPago) > 0 &&
        (payment.pagoCuota || this.customer == "clab")
      )
        return "Pendiente";
      return "—";
    },
    canPay(payment) {
      return (
        this.country == 1 &&
        parseFloat(payment.montoPago) > 0 &&
        this.project.activoPagosWeb &&
        !["Crédito Hipotecario", "Pie Crédito Directo", "Pie Promesa"].includes(
          payment.descripcionPago
        )
      );
    },
    text_payed(payment) {
      if (payment.reciboPago.length > 0) {
        return ["CANCELADO", "DEPOSITADO"].includes(
          payment.reciboPago[0].estadoPago
        )
          ? "Pagado"
          : payment.reciboPago[0].estadoPago;
      }
    },
    webpay_payment() {
      this.transbank.loading = true;
      this.axios
        .post("payments/webpay", { transbank: this.transbank })
        .then((result) => {
          this.transbank.token = result.data.token;
          this.transbank.url = result.data.url;
          this.transbank.loading = false;
          if (this.transbank.token)
            document.getElementById("webpay-token").value = result.data.token;
          document.getElementById("webpay-form").action = result.data.url;
          document.getElementById("webpay-form").submit();
        })
        .catch(() => {
          this.transbank.loading = false;
        });
    },
    onChangePage(pageOfItems) {
      this.pageOfItems = pageOfItems;
      if (this.isFirstLoad) {
        this.isFirstLoad = false;
        return;
      }
      this.$nextTick(() => {
        if (this.$refs.paymentsWrapper) {
          this.$refs.paymentsWrapper.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      });
    },
    showModal(payment) {
      this.$refs["my-modal"].show();
      this.transbank.id = payment.codigo;
      this.transbank.monto = parseInt(payment.montoPago * payment.valorUF);
    },
    is_payed(payment) {
      if (payment.reciboPago === undefined) {
        return false;
      } else {
        return payment.reciboPago.length > 0 ? true : false;
      }
    },
  },
  computed: {
    paidCount() {
      return this.payment_data.payments.filter((p) => this.is_payed(p)).length;
    },
    pendingCount() {
      return this.payment_data.payments.filter(
        (p) =>
          !this.is_payed(p) &&
          parseFloat(p.montoPago) > 0 &&
          p.fechaVencimientoPago >= this.date_today
      ).length;
    },
    overdueCount() {
      return this.payment_data.payments.filter(
        (p) =>
          !this.is_payed(p) &&
          parseFloat(p.montoPago) > 0 &&
          p.fechaVencimientoPago < this.date_today &&
          ![
            "Crédito Hipotecario",
            "Pie Crédito Directo",
            "Pie Promesa",
          ].includes(p.descripcionPago)
      ).length;
    },
    customer() {
      return this.$store.getters.getCustomer;
    },
    customerId() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.customer_id
        : null;
    },
    country() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.country
        : 1;
    },
    project() {
      return this.$store.getters.getProjectByMoby(this.apartment.project_id);
    },
    apartment() {
      return this.$store.getters.getApartmentById(parseInt(this.asset_id));
    },
    date_today() {
      return this.$moment(new Date()).format("YYYY-MM-DD");
    },
    automated() {
      return this.$store.getters.getAutomatedPayments;
    },
    gcVersion() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.gc_version
        : 1;
    },
    integrationUrl() {
      return this.$store.getters.getIntegrationUrl;
    },
  },
};
</script>
<style scoped>
/* ===== Payments Wrapper ===== */
.payments-wrapper {
  padding: 0;
}

/* ===== Payments Container ===== */
.payments-container {
  padding: 0;
}

/* ===== Header ===== */
.payments-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  margin-bottom: 24px;
  gap: 20px;
  flex-wrap: wrap;
}

.contract-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.contract-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: var(--color-btn, #3b82f6);
  border-radius: 12px;
  color: white;
}

.contract-details {
  display: flex;
  flex-direction: column;
}

.contract-label {
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

.contract-number {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
}

/* Payment Summary */
.payment-summary {
  display: flex;
  align-items: center;
  gap: 20px;
  background: white;
  padding: 12px 20px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.summary-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 60px;
}

.summary-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #10b981;
}

.summary-value.warning {
  color: #f59e0b;
}

.summary-value.danger {
  color: #ef4444;
}

.summary-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.summary-divider {
  width: 1px;
  height: 32px;
  background: #e5e7eb;
}

/* ===== Table Styles ===== */
.payments-table-wrapper {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
}

.payments-table {
  width: 100%;
  border-collapse: collapse;
}

.payments-table thead tr {
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.payments-table th {
  padding: 14px 20px;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.payments-table tbody tr {
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.15s ease;
}

.payments-table tbody tr:last-child {
  border-bottom: none;
}

.payments-table tbody tr:hover {
  background: #f9fafb;
}

.payments-table tbody tr.row-paid {
  background: #f0fdf4;
}

.payments-table tbody tr.row-overdue {
  background: #fef2f2;
}

.payments-table td {
  padding: 16px 20px;
  font-size: 0.9375rem;
  color: #374151;
}

/* Description */
.description-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.description-text {
  font-weight: 500;
  color: #1f2937;
}

.description-quota {
  font-size: 0.8125rem;
  color: #6b7280;
}

/* Amount */
.payment-amount {
  font-weight: 600;
  color: #1f2937;
}

.amount-currency {
  font-size: 0.8125rem;
  color: #6b7280;
  margin-left: 4px;
}

/* Date */
.payment-date {
  color: #6b7280;
}

/* Status Badge */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.status-badge.paid {
  background: #dcfce7;
  color: #15803d;
}

.status-badge.overdue {
  background: #fee2e2;
  color: #dc2626;
}

.status-badge.pending {
  background: #fef3c7;
  color: #d97706;
}

.status-badge.scheduled {
  background: #e0e7ff;
  color: #4f46e5;
}

.status-badge.neutral {
  background: #f3f4f6;
  color: #9ca3af;
}

/* Action Buttons */
.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
}

.action-btn.receipt {
  background: #f3f4f6;
  color: #374151;
}

.action-btn.receipt:hover {
  background: #e5e7eb;
  text-decoration: none;
  color: #1f2937;
}

.action-btn.pay {
  background: var(--color-btn, #3b82f6);
  color: white;
}

.action-btn.pay:hover {
  opacity: 0.9;
  transform: translateY(-1px);
  text-decoration: none;
  color: white;
}

.action-btn.full-width {
  width: 100%;
  justify-content: center;
}

.no-action {
  color: #d1d5db;
}

/* ===== Mobile Cards ===== */
.payments-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.payment-card {
  background: white;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
}

.payment-card.row-paid {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.payment-card.row-overdue {
  background: #fef2f2;
  border-color: #fecaca;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.card-title {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.card-body {
  display: flex;
  gap: 24px;
  padding: 12px 0;
  border-top: 1px solid #f3f4f6;
  border-bottom: 1px solid #f3f4f6;
  margin-bottom: 12px;
}

.card-detail {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detail-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.detail-value {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1f2937;
}

.card-footer {
  display: flex;
  gap: 8px;
}

/* ===== Pagination ===== */
.payments-pagination {
  margin-top: 24px;
  display: flex;
  justify-content: center;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding: 8px 0;
}

.payments-pagination :deep(.pagination) {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  list-style: none;
  padding: 0;
  margin: 0;
  justify-content: center;
}

.payments-pagination :deep(.page-item) {
  display: inline-block;
}

.payments-pagination :deep(.page-link) {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border-radius: 8px;
  color: #4b5563;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;
  border: 1px solid #e5e7eb;
  background: #ffffff;
}

.payments-pagination :deep(.page-item.active .page-link) {
  background: var(--color-btn, #3b82f6);
  color: white;
  border-color: var(--color-btn, #3b82f6);
}

.payments-pagination :deep(.page-link:hover) {
  background: #f3f4f6;
}

@media (max-width: 576px) {
  .payments-pagination :deep(.pagination) {
    gap: 3px;
  }
  .payments-pagination :deep(.page-link) {
    min-width: 32px;
    height: 32px;
    padding: 0 8px;
    font-size: 0.8125rem;
    border-radius: 6px;
  }
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .payments-header {
    flex-direction: column;
    align-items: stretch;
    padding: 16px;
  }

  .contract-info {
    justify-content: center;
  }

  .payment-summary {
    justify-content: center;
  }

  .summary-item {
    min-width: 50px;
  }

  .summary-value {
    font-size: 1.25rem;
  }
}

@media (max-width: 576px) {
  .payments-header {
    padding: 12px;
    border-radius: 12px;
    margin-bottom: 16px;
  }

  .contract-icon {
    width: 40px;
    height: 40px;
  }

  .contract-icon svg {
    width: 20px;
    height: 20px;
  }

  .contract-number {
    font-size: 1.125rem;
  }

  .payment-summary {
    padding: 10px 16px;
    gap: 16px;
  }

  .summary-divider {
    height: 24px;
  }

  .payment-card {
    padding: 14px;
    border-radius: 12px;
  }

  .card-body {
    flex-direction: column;
    gap: 12px;
  }
}

/* ===== Skeleton Styles ===== */
@keyframes skeleton-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

.skeleton-header {
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.skeleton-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 12px;
}

.skeleton-text {
  height: 14px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 6px;
}

.skeleton-text.skeleton-label {
  width: 60px;
  height: 10px;
  margin-bottom: 6px;
}

.skeleton-text.skeleton-title {
  width: 120px;
  height: 20px;
}

.skeleton-text.skeleton-number {
  width: 40px;
  height: 24px;
  margin-bottom: 4px;
}

.skeleton-text.skeleton-small {
  height: 10px;
  width: 60px;
}

.skeleton-badge {
  width: 80px;
  height: 28px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 20px;
}

.skeleton-button {
  width: 90px;
  height: 36px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 8px;
}

.skeleton-button-full {
  width: 100%;
  height: 40px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 8px;
}

.skeleton-row td {
  padding: 16px 20px;
}

.skeleton-card {
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.skeleton-card .card-body {
  border-color: #e5e7eb;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
